import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { plans as plansApi, auth } from "../lib/api";
import {
  Disc3,
  Users,
  Radio,
  PlayCircle,
  Sparkles,
  ListMusic,
  Share2,
} from "lucide-react";

const HERO_SHOT = "/screens/hero.jpg";
const FEATURE_SHOTS = {
  silence: "/screens/dj_console.jpg",
  dj: "/screens/dj_decks.jpg",
  friends: "/screens/friends.jpg",
};

export default function Landing() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    plansApi.list().then(setPlans).catch(() => {});
    auth
      .me()
      .then(() => nav("/app"))
      .catch(() => setChecking(false));
  }, [nav]);

  const goLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const scrollToPlans = () => {
    document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white font-mono text-xs tracking-widest">
        LOADING...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden grain">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full float-slow"
          style={{
            background: "radial-gradient(circle, rgba(217,70,239,0.25), transparent 60%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute -bottom-32 -right-32 w-[700px] h-[700px] rounded-full float-slow"
          style={{
            background: "radial-gradient(circle, rgba(6,182,212,0.22), transparent 60%)",
            filter: "blur(60px)",
            animationDelay: "-6s",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-10 sm:mb-16">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #d946ef, #06b6d4)",
                boxShadow: "0 0 24px rgba(217,70,239,0.5)",
              }}
            >
              <Disc3 className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading font-black text-lg">HOUSEPARTY</span>
          </div>
          <button
            onClick={goLogin}
            className="text-sm text-zinc-400 hover:text-white transition"
            data-testid="header-login-btn"
          >
            Log in →
          </button>
        </header>

        {/* Hero — copy + hero video card */}
        <section className="grid grid-cols-12 gap-6 mb-16 sm:mb-24">
          <div className="col-span-12 lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-950/50 mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
              <span className="font-mono text-[10px] tracking-widest text-zinc-400">
                LIVE FROM YOUR LIVING ROOM
              </span>
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
              Turn any room <br />
              into a{" "}
              <span
                style={{
                  background: "linear-gradient(90deg,#d946ef,#06b6d4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                nightclub.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-lg leading-relaxed mb-8">
              Two decks. A real crossfader. Pull tracks straight from YouTube, share a link, and
              let your friends stack the queue.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={scrollToPlans}
                className="px-6 py-3 rounded-lg font-mono text-xs font-black tracking-widest transition-all active:scale-95"
                style={{
                  background: "linear-gradient(135deg,#d946ef,#06b6d4)",
                  color: "#050505",
                  boxShadow: "0 0 24px rgba(217,70,239,0.4)",
                }}
                data-testid="hero-cta-primary"
              >
                START A PARTY →
              </button>
              <button
                onClick={goLogin}
                className="px-6 py-3 rounded-lg font-mono text-xs font-bold tracking-widest border border-zinc-700 text-white hover:bg-zinc-900 transition"
                data-testid="hero-cta-login"
              >
                SIGN IN
              </button>
            </div>
          </div>

          {/* Hero screenshot card */}
          <div className="col-span-12 lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950">
              <img
                src={HERO_SHOT}
                alt="House Party DJ Console — two decks, waveforms, crossfader and live queue"
                className="w-full h-auto object-cover block"
                data-testid="hero-shot"
              />
              {/* Neon corner glows */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 15% 20%, rgba(217,70,239,0.10), transparent 50%), radial-gradient(circle at 85% 80%, rgba(6,182,212,0.12), transparent 50%)",
                  mixBlendMode: "screen",
                }}
              />
              {/* Ribbon */}
              <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10">
                <div className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
                <span className="font-mono text-[9px] tracking-widest text-white">
                  LIVE · YOUR DJ CONSOLE
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Feature strip - 2 boxes */}
        <section className="mb-16 sm:mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <FeatureCard
              title="Be the DJ of your party"
              copy="Two decks. A real crossfader. Tap-tempo BPM and waveform scrubbing. Flip on Auto-DJ and the music never stops — one track fades out as the next fades in. Break the silence, all night."
              icon={<Disc3 className="w-4 h-4" />}
              accent="#d946ef"
              imageUrl={FEATURE_SHOTS.dj}
              testId="feature-dj"
            />
            <FeatureCard
              title="Let friends add to the vibe"
              copy="Share a single invite link. Guests search YouTube and drop songs into the queue live — no download, no login, no drama. Everyone becomes part of the setlist."
              icon={<Users className="w-4 h-4" />}
              accent="#06b6d4"
              imageUrl={FEATURE_SHOTS.friends}
              testId="feature-friends"
            />
          </div>
        </section>

        {/* How it works */}
        <section className="mb-16 sm:mb-24">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">
                HOW IT WORKS
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">
                From silent room to sold-out show in 3 taps.
              </h2>
            </div>
            <PlayCircle className="w-8 h-8 text-fuchsia-400 hidden sm:block" />
          </div>
          <div className="grid grid-cols-12 gap-4">
            <Step
              num="01"
              title="Pick a plan"
              copy="Single-night pass ₹99 or Monthly pass ₹499. Pay with Cashfree — cards, UPI, wallets. Instant unlock."
              icon={<Sparkles className="w-5 h-5" />}
              accent="#eab308"
            />
            <Step
              num="02"
              title="Build the queue"
              copy="Search YouTube, paste any link, or import a whole playlist. Send the invite link to your friends."
              icon={<ListMusic className="w-5 h-5" />}
              accent="#d946ef"
            />
            <Step
              num="03"
              title="Drop the beat"
              copy="Load Deck A. Load Deck B. Slide the crossfader. Enable Auto-DJ if you'd rather just enjoy the night."
              icon={<Share2 className="w-5 h-5" />}
              accent="#06b6d4"
            />
          </div>
        </section>

        {/* Plans (Tetris) */}
        <section id="plans">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-5">
              <h2 className="font-heading font-black text-3xl sm:text-4xl mb-2">
                Pick your ticket.
              </h2>
              <p className="text-sm text-zinc-500 max-w-xs">
                No subscription trap. Pay per party, or a month of infinite parties.
              </p>
              <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
                <Radio className="w-4 h-4" />
                <span className="font-mono tracking-widest text-[10px]">
                  POWERED BY CASHFREE · TEST MODE
                </span>
              </div>
            </div>

            {plans.map((p) => {
              const isMonthly = p.id === "monthly_pass_499";
              const gradient = isMonthly
                ? "linear-gradient(135deg, rgba(6,182,212,0.15), rgba(217,70,239,0.10))"
                : "linear-gradient(135deg, rgba(217,70,239,0.10), rgba(6,182,212,0.05))";
              const accent = isMonthly ? "#06b6d4" : "#d946ef";
              return (
                <div
                  key={p.id}
                  className={`relative rounded-2xl p-6 border overflow-hidden ${
                    isMonthly
                      ? "col-span-12 lg:col-span-4 lg:row-span-2"
                      : "col-span-12 sm:col-span-6 lg:col-span-3"
                  }`}
                  style={{
                    background: gradient,
                    borderColor: isMonthly ? `${accent}55` : "#27272a",
                    boxShadow: isMonthly ? `0 0 40px ${accent}22` : "none",
                  }}
                  data-testid={`plan-card-${p.id}`}
                >
                  {isMonthly && (
                    <div className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-yellow-500 text-black text-[9px] font-mono font-black tracking-widest">
                      BEST VALUE
                    </div>
                  )}
                  <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-3">
                    {isMonthly ? "MONTHLY PASS" : "ONE-NIGHT ACCESS"}
                  </div>
                  <div
                    className="font-heading font-black text-4xl sm:text-5xl mb-1"
                    style={{ color: accent }}
                  >
                    ₹{p.amount}
                  </div>
                  <div className="text-xs text-zinc-500 mb-4">
                    {isMonthly ? "for 30 days" : "for one party"}
                  </div>
                  <div className="text-sm text-zinc-300 mb-5">{p.description}</div>

                  <ul className="space-y-2 text-xs text-zinc-400 mb-6">
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full" style={{ background: accent }} />
                      Two-deck mixer with crossfader
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full" style={{ background: accent }} />
                      Waveform scrubbing + tap-tempo BPM
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full" style={{ background: accent }} />
                      Auto-DJ crossfading
                    </li>
                    {isMonthly && (
                      <>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full" style={{ background: accent }} />
                          Unlimited parties
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1 h-1 rounded-full" style={{ background: accent }} />
                          Collab invite links
                        </li>
                      </>
                    )}
                  </ul>

                  <button
                    onClick={goLogin}
                    className="w-full py-3 rounded-lg font-mono text-xs font-bold tracking-widest transition-all active:scale-[0.98]"
                    style={{
                      background: isMonthly ? accent : "#0a0a0a",
                      color: isMonthly ? "#050505" : "#fff",
                      border: `1px solid ${isMonthly ? accent : "#27272a"}`,
                      boxShadow: isMonthly ? `0 0 20px ${accent}66` : "none",
                    }}
                    data-testid={`buy-btn-${p.id}`}
                  >
                    {isMonthly ? "GET MONTHLY PASS →" : "BUY & LOGIN →"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <footer className="mt-24 pb-8 flex items-center justify-between text-xs text-zinc-600">
          <span className="font-mono tracking-widest">© {new Date().getFullYear()} HOUSEPARTY</span>
          <span className="font-mono tracking-widest">MADE FOR LATE NIGHTS</span>
        </footer>
      </div>
    </div>
  );
}

function FeatureCard({ title, copy, icon, accent, imageUrl, testId }) {
  return (
    <div
      className="rounded-2xl border border-zinc-800 bg-zinc-950/70 overflow-hidden group h-full flex flex-col"
      data-testid={testId}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-zinc-900">
        <img
          src={imageUrl}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
        <div
          className="absolute top-3 left-3 w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: accent, boxShadow: `0 0 16px ${accent}88` }}
        >
          <span className="text-black">{icon}</span>
        </div>
      </div>
      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <div className="font-heading font-black text-lg mb-1.5 text-white">
          {title}
        </div>
        <div className="text-xs sm:text-sm text-zinc-400 leading-relaxed">{copy}</div>
      </div>
    </div>
  );
}

function Step({ num, title, copy, icon, accent }) {
  return (
    <div
      className="col-span-12 sm:col-span-4 rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5 relative overflow-hidden"
      data-testid={`step-${num}`}
    >
      <div
        className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-15"
        style={{ background: accent, filter: "blur(20px)" }}
      />
      <div className="flex items-center gap-3 mb-3 relative">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}22`, border: `1px solid ${accent}55`, color: accent }}
        >
          {icon}
        </div>
        <div className="font-mono text-[10px] tracking-widest text-zinc-500">STEP {num}</div>
      </div>
      <div className="font-heading font-black text-xl mb-1.5 relative">{title}</div>
      <div className="text-xs sm:text-sm text-zinc-400 leading-relaxed relative">{copy}</div>
    </div>
  );
}
